import React, { useMemo } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import InputField from "../../../../../components/InputFields/InputField";
import ErrorMessage from "../../../../../components/Messages/ErrorMessage";
import EspSkeleton from "../../common/EspSkeleton";
import Image from "../../../../../components/Image/Image";
import { GREY } from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import { STRINGS } from "../../strings";
import CustomTooltip from "../../../../../components/Tooltip/CustomTooltip";
import { IconComponent } from "../../../../../components/Icon";
import GroupSenderPicker from "./GroupSenderPicker";
import RadioButton from "../../../../../components/RadioButton/RadioButton";
import Checkbox from "../../../../../components/Buttons/Checkbox/Checkbox";
import { isOAuthMailboxEsp } from "../../../../../utils/constants";

const LabelWithTooltip = ({ label, tooltip }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
    <TypographyInline
      size={"xs"}
      color={GREY.QUATINARY}
      fontWeight={fontWeight.MEDIUM}
      label={label}
    />
    <CustomTooltip title={tooltip} variant="top">
      <Box sx={{ display: "inline-flex", cursor: "help" }}>
        <IconComponent
          iconLabel={ICON_LABELS.INFO}
          color={GREY.TERTIARY}
          fontSize={"14px"}
        />
      </Box>
    </CustomTooltip>
  </Box>
);

const SENDING_ACCOUNTS_TOOLTIP =
  "Select the connected mailboxes used to send this campaign. Each mailbox's default signature is applied automatically. Update a mailbox's signature in mailbox settings.";

// Checkbox + short label, with the explanation parked in a tooltip so both
// options cost one line instead of two description blocks.
const EmailOption = ({ label, tooltip, checked, onToggle }) => (
  <Box sx={stepStyles.option}>
    <Checkbox
      noMargin
      checked={checked}
      onChange={onToggle}
      aria-label={label}
    />
    <Box sx={stepStyles.optionLabel} onClick={onToggle}>
      <TypographyInline
        size={"xs"}
        color={GREY.QUATINARY}
        fontWeight={fontWeight.MEDIUM}
        label={label}
      />
    </Box>
    <CustomTooltip title={tooltip} variant="top">
      <Box sx={stepStyles.optionInfo}>
        <IconComponent
          iconLabel={ICON_LABELS.INFO}
          color={GREY.TERTIARY}
          fontSize={"14px"}
        />
      </Box>
    </CustomTooltip>
  </Box>
);

const BasicDetailsStep = ({
  classes,
  name,
  setName,
  validations,
  unsubscribeEnabled,
  setUnsubscribeEnabled,
  trackingEnabled,
  setTrackingEnabled,
  loading,
  espList,
  selectedEsps,
  setSelectedEsps,
  setItemData,
  itemData,
  espConnectionList,
  handleConnectMail,
  outList,
  mailList,
  zohoList,
  signaturesByEmail,
  signatureListByEmail,
  signatureLoadingByEmail,
  onToggleSenderEmail,
  selectedSenderEmails,
  senderMode,
  setSenderMode,
  groupsEnabled,
  groups,
  groupsLoading,
  selectedGroupIds,
  groupMembersById,
  groupMembersLoadingById,
  excludedEmailsByGroupId,
  onToggleGroup,
  onExpandGroup,
  onToggleGroupMember,
  currentStep,
  totalSteps,
}) => {
  const navigate = useNavigate();
  const isMultiEmailEsp = selectedEsps.some((esp) => isOAuthMailboxEsp(esp));
  // No provider selected at all: keep the same Group/Individual picker shell
  // visible (rather than falling back to the single "Sending account" block),
  // but both dropdowns show a "select a provider" prompt instead of any data.
  const noProviderSelected = selectedEsps.length === 0;
  // Combined mailbox pool across every selected provider - each row already
  // carries its own real esp (2/5/8), so mixing Gmail + Zoho in the same
  // dropdown is just a concat, never a lossy single-provider filter.
  const availableEmails = useMemo(() => {
    const lists = [];
    if (selectedEsps.includes(2)) lists.push(...mailList);
    if (selectedEsps.includes(5)) lists.push(...outList);
    if (selectedEsps.includes(8)) lists.push(...zohoList);
    return lists;
  }, [selectedEsps, mailList, outList, zohoList]);
  // GMAIL SEND-AS ALIAS: account email -> chosen alias, for DISPLAY only.
  // Group members come from the group APIs (which know nothing about aliases),
  // so the picker needs this map to label them; selection/payload values stay
  // the account email everywhere. Empty for mailboxes without an alias.
  const aliasByEmail = useMemo(() => {
    const map = {};
    [...mailList, ...outList, ...zohoList].forEach((m) => {
      if (m.alias) map[m.label] = m.alias;
    });
    return map;
  }, [mailList, outList, zohoList]);
  // esp -> provider icon, so a mixed-provider list still shows which mailbox
  // belongs to which ESP at a glance.
  const espIconByEsp = useMemo(() => {
    const map = {};
    (espList || []).forEach((e) => {
      map[e.id] = e.image;
    });
    return map;
  }, [espList]);

  // Two sender sources, switched by a radio. "group" shows mailbox groups
  // (group APIs only); "standalone" shows individual connected mailboxes
  // (individual-mailbox API only). The radio only appears for multi-mailbox
  // providers (Gmail / Outlook / Zoho); other ESPs are single-email via itemData.
  const SENDER_MODE_ITEMS = [
    { id: "group", label: "Group" },
    { id: "standalone", label: "Individual" },
  ];

  return (
    <Box data-tour="campaign-basic-details-section">
      <div className={classes.div}>
        <TypographyInline
          size={"md"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.SEMIBOLD}
          label={STRINGS.CAMP_BASIC_DETAILS}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={
            currentStep
              ? `Step ${currentStep} of ${totalSteps}`
              : STRINGS.STEP15
          }
        />
      </div>
      <Box className={classes.containStep}>
        <div className={classes.subTitles}>
          <TypographyInline
            size={"sm"}
            color={GREY.QUATINARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.COMMON_NAME}
          />
        </div>

        <div className={classes.mTop}>
          <InputField
            value={name}
            placeholder={STRINGS.ENTER_CAMP_NAME}
            size={"sm36"}
            onChange={(e) => setName(e.target.value)}
          />
          {validations?.name && (
            <ErrorMessage message={validations?.name || ""} />
          )}
        </div>

        <Box
          sx={stepStyles.optionRow}
          data-tour="campaign-email-options-section"
        >
          <EmailOption
            label={STRINGS.UNSUB_TAG_LABEL}
            tooltip={STRINGS.UNSUB_TAG_DESC}
            checked={unsubscribeEnabled}
            onToggle={() => setUnsubscribeEnabled(!unsubscribeEnabled)}
          />
          <EmailOption
            label={STRINGS.ENABLE_TRACKING_LABEL}
            tooltip={STRINGS.ENABLE_TRACKING_DESC}
            checked={trackingEnabled}
            onToggle={() => setTrackingEnabled(!trackingEnabled)}
          />
        </Box>

        <Box className={classes.containEachInfoo} data-tour="campaign-send-via-section">
          <TypographyInline
            size={"sm"}
            color={GREY.QUATINARY}
            fontWeight={fontWeight.MEDIUM}
            label={STRINGS.SENDVIA}
          />

          {loading ? (
            <EspSkeleton />
          ) : espList?.length ? (
            <ul
              className="ul1"
              id="modal_body"
              style={{ paddingBottom: "12px" }}
            >
              {espList?.map((item) => (
                <li className={classes.sendViaContainer} key={item.id}>
                  <input
                    type="checkbox"
                    style={{ display: "none" }}
                    id={`${STRINGS.ESP}${item.id}`}
                    className="input"
                    checked={selectedEsps.includes(item?.id)}
                    value={item?.label}
                    name={item.id}
                    onClick={() => {
                      // Multi-select: none, one, or several providers at
                      // once. No API calls happen here - mailList/outList/
                      // zohoList are already loaded once at mount, so toggling
                      // a card just changes which of them get merged into the
                      // Individual dropdown.
                      setSelectedEsps((prev) =>
                        prev.includes(item?.id)
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id],
                      );
                      setItemData(item);
                    }}
                    readOnly
                  />
                  <label
                    className="label"
                    style={{
                      padding:
                        item.label === STRINGS.CONNECT_GOOGLE
                          ? "22px 8px"
                          : ".729 .833vw",
                      textAlign: "center",
                    }}
                    for={`${STRINGS.ESP}${item.id}`}
                  >
                    <Image
                      width={
                        item.label === STRINGS.CONNECT_GOOGLE ? "89px" : "100px"
                      }
                      height={
                        item.label === STRINGS.CONNECT_GOOGLE ? "28px" : "70px"
                      }
                      src={item?.image}
                      alt={STRINGS.ESP}
                    />
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <Box className={classes.containNavigationBtns}>
              <Box className={classes.bodyContainerEsp}>
                {espConnectionList?.map((val) => (
                  <button
                    key={val.id}
                    className={classes.socialAuthBtn}
                    onClick={() => handleConnectMail(val?.id)}
                  >
                    <Image src={val.icon} className={classes.socialAuthIcon} />
                    {val.label}
                  </button>
                ))}
              </Box>
            </Box>
          )}

          {espList?.length ? (
            isMultiEmailEsp || noProviderSelected ? (
              <Box className={classes.mailStyle} sx={{ width: "100%" }}>
                <LabelWithTooltip
                  label="Add a mailbox via :"
                  tooltip={SENDING_ACCOUNTS_TOOLTIP}
                />
                <Box sx={{ marginBottom: "8px" }}>
                  <RadioButton
                    items={SENDER_MODE_ITEMS}
                    value={senderMode}
                    onChange={(e) => setSenderMode(e.target.value)}
                  />
                </Box>
                <GroupSenderPicker
                  mode={senderMode}
                  groups={groups}
                  groupsLoading={groupsLoading}
                  selectedGroupIds={selectedGroupIds}
                  membersById={groupMembersById}
                  membersLoadingById={groupMembersLoadingById}
                  excludedEmailsByGroupId={excludedEmailsByGroupId}
                  onToggleGroup={onToggleGroup}
                  onExpandGroup={onExpandGroup}
                  onToggleMember={onToggleGroupMember}
                  onOpenSettings={() =>
                    navigate("/user/settings?tab=CampaignDefaults")
                  }
                  // Standalone mode: individual connected mailboxes, merged
                  // across every selected provider.
                  availableEmails={availableEmails}
                  aliasByEmail={aliasByEmail}
                  espIconByEsp={espIconByEsp}
                  noProviderSelected={noProviderSelected}
                  selectedSenderEmails={selectedSenderEmails}
                  onToggleSenderEmail={onToggleSenderEmail}
                  signaturesByEmail={signaturesByEmail}
                  signatureListByEmail={signatureListByEmail}
                  signatureLoadingByEmail={signatureLoadingByEmail}
                />
              </Box>
            ) : (
              <Box className={classes.mailStyle}>
                <LabelWithTooltip
                  label="Sending account*"
                  tooltip={SENDING_ACCOUNTS_TOOLTIP}
                />
                <Box className={classes.previewStepDetailSubContainer1}>
                  <div>
                    <TypographyInline
                      size={"sm"}
                      color={GREY.DISABLED}
                      fontWeight={fontWeight.REGULAR}
                      label={itemData?.email}
                    />
                  </div>
                </Box>
              </Box>
            )
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const stepStyles = {
  optionRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px 24px",
    marginTop: "10px",
  },
  option: { display: "flex", alignItems: "center", gap: "6px" },
  optionLabel: { cursor: "pointer", whiteSpace: "nowrap" },
  optionInfo: { display: "inline-flex", cursor: "help" },
};

export default React.memo(BasicDetailsStep);
